import React, { useMemo } from 'react';

import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { PipelineV3HumanNodeStatus } from '@scale/shared/pipelinev3/humanNodeStatus';
import { ISensorFusionSubstageInstanceLookupDto } from '@scale/sensor-fusion-shared';
import { SubstageInstanceStatus } from '@scale/shared-types';

import { LookupIdLink } from '../common';
import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type SubstageInstanceOverviewProps = {
  substageInstance: ISensorFusionSubstageInstanceLookupDto;
};

const SubstageInstanceOverviewPanel = ({ substageInstance }: SubstageInstanceOverviewProps) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};

  const { stageInstance, substageDefinition, humanNode, project } = substageInstance;
  const humanNodeId = humanNode?.id;
  const substageInstanceStatus =
    humanNode?.status === PipelineV3HumanNodeStatus.Paused ? 'paused' : substageInstance.status;

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  return (
    <div className="border-solid border border-neutral-200 px-4 py-4 my-4 w-full overflow-wrap-anywhere">
      <h4 className="font-bold text-lg">Overview</h4>
      <div className="flex justify-between">
        <div className="flex flex-col w-2/3 gap-2 py-1">
          <div>
            <span className="font-medium">Unified Task: </span>
            <LookupIdLink className="inline-block" id={substageInstance.taskId} />
          </div>
          <div>
            <span className="font-medium">Project: </span>
            {`${project.name} `}
            <WithCopy className="inline-block text-info-700" copyText={project.id}>
              <a href={`${toolUrl}/${project.id}`}>{project.id}</a>
            </WithCopy>
          </div>
          <div>
            <span className="font-medium">Type: </span>
            {substageDefinition.childTaskType}
          </div>
          <div>
            <span className="font-medium">Status: </span>
            {substageInstanceStatus}
          </div>
          <div>
            <span className="font-medium">Substage Definition: </span>
            {`${substageDefinition.name} `}
            <WithCopy className="inline-block text-info-700" copyText={substageDefinition.id}>
              <a href={`${toolUrl}/${substageDefinition.id}`}>{substageDefinition.id}</a>
            </WithCopy>
          </div>
          <div>
            <span className="font-medium">Stage Instance: </span>
            {`${stageInstance.defName} `}
            <WithCopy className="inline-block text-info-700" copyText={stageInstance.id}>
              <a href={`${toolUrl}/${stageInstance.id}`}>{stageInstance.id}</a>
            </WithCopy>
          </div>
          {humanNodeId ? (
            <div className="flex flex-col">
              {substageInstance?.workerInfo?.id && (
                <div className="py-1">
                  <span className="font-medium">Worker: </span>
                  {substageInstance?.workerInfo?.email && (
                    <text>{substageInstance.workerInfo.email}</text>
                  )}
                  <LookupIdLink className="inline-block" id={substageInstance?.workerInfo?.id} />
                </div>
              )}
              <span className="font-medium">Human Node: </span>
              <LookupIdLink className="inline-block" id={humanNodeId} />
            </div>
          ) : undefined}
          {substageInstance.status === SubstageInstanceStatus.InProgress && (
            <div>
              <span className="font-medium">Review Level: </span>
              <text>{substageInstance?.reviewLevel}</text>
            </div>
          )}
          {substageInstance.params.sceneId || !substageInstance.params.sceneUrl ? (
            <div>
              <span className="font-medium">Scene ID: </span>
              {substageInstance.params.sceneId ? (
                <LookupIdLink className="inline-block" id={substageInstance.params.sceneId} />
              ) : (
                'None'
              )}
            </div>
          ) : (
            <WithCopy className="inline-block" copyText={substageInstance.params.sceneUrl}>
              <a className="text-info-700" href={substageInstance.params.sceneUrl}>
                Scene URL
              </a>
            </WithCopy>
          )}
          <div>
            <span className="font-medium">Created At: </span>
            {new Date(substageInstance.createdAt).toLocaleString()}
          </div>
          {substageInstance.completedAt && (
            <div>
              <span className="font-medium">Completed At: </span>
              {new Date(substageInstance.completedAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default SubstageInstanceOverviewPanel;
